import React, { useContext, useMemo } from "react";
import { MessageCircle } from "lucide-react";

import { AuthContext, IntlContext } from "context";
import { useProviderServiceContact } from "hooks/useProviderContact";
import { ProviderContactServiceDialog } from "components/provider/ProviderContactServiceDialog";
import { useScrollTo } from "hooks/useScrollTo";
import { isProviderAvailable } from "helpers/isProviderAvailable";
import { PROVIDER_SERVICES_SECTION_ID } from "./ProviderServicesListCard";
import type { OurServiceInterface } from "types/service";
import { ProviderIdentity } from "components/provider/ProviderIdentity";
import { ProviderServiceChips } from "components/provider/ProviderServiceChips";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { Role } from "types/user";
import FavoriteProviderButton from "components/Favorite/FavoriteProviderButton";

interface Props {
    service: OurServiceInterface;
}

export const ProviderProfileHeader: React.FC<Props> = ({ service }) => {
    const intl = useContext(IntlContext);
    const { account, currentRole } = useContext(AuthContext);
    const { provider } = service;
    const catalog = useMemo(() => service.provider_services ?? [], [service.provider_services]);
    const { open, setOpen, onContactClick, contactService } = useProviderServiceContact(catalog, service.id);
    const isOwnProfile = account?.resources?.provider?.[0]?.id === service.provider_id;
    const isAvailable = isProviderAvailable(service);
    const showContactButton = currentRole !== Role.PROVIDER && !isOwnProfile && isAvailable;
    const displayPrice = Number(service.price_from ?? service.price);
    const scrollToServices = useScrollTo(PROVIDER_SERVICES_SECTION_ID);

    return (
        <Card variant="detail" className="p-6">
            <div className="flex flex-col gap-6 lg:flex-row">
                <div className="min-w-0 flex-1 space-y-7">
                    <ProviderIdentity provider={provider} size="lg" />

                    <ProviderServiceChips
                        className="ml-16"
                        catalog={catalog}
                        iconClassName="h-3 w-3"
                        onChipClick={scrollToServices}
                    />
                </div>

                <div className="shrink-0 border-border/40 lg:border-l lg:pl-10">
                    <div className="flex flex-col gap-5">
                        <div className="flex items-start justify-end gap-2">
                            <FavoriteProviderButton
                                providerId={service.provider_id}
                                clientsFav={service.clients_fav}
                            />
                        </div>
                        <div className="flex flex-col items-center rounded-xl bg-muted/30 px-4 py-4 text-center">
                            <p className="text-xs font-medium text-muted-foreground">
                                {catalog.length > 1
                                    ? intl.formatMessage("provider.card.price_from")
                                    : intl.formatMessage("provider.view.hourly_rate")}
                            </p>
                            <p className="mt-0.5 text-3xl font-extrabold leading-tight text-foreground">
                                {displayPrice.toFixed(2)}€
                                <span className="text-base font-normal text-muted-foreground">
                                    {intl.formatMessage("provider.card.price_per_hour")}
                                </span>
                            </p>
                        </div>
                        {showContactButton ? (
                            <>
                                <Button size="lg" className="w-full gap-2" onClick={onContactClick}>
                                    <MessageCircle className="h-4 w-4" />
                                    {intl.formatMessage("provider.card.contact", { firstName: provider.first_name })}
                                </Button>
                                <ProviderContactServiceDialog
                                    open={open}
                                    onOpenChange={setOpen}
                                    services={catalog}
                                    firstName={provider.first_name}
                                    onSelect={contactService}
                                />
                            </>
                        ) : (
                            !isAvailable && (
                                <Badge variant="unavailable" className="w-full justify-center py-2.5">
                                    {intl.formatMessage("provider.card.unavailable")}
                                </Badge>
                            )
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
};
